import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Moon, Flame, Skull } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import RadioPlayer from "@/components/RadioPlayer";
import { ASSETS, TEXTURES, GOOGLE_FORM_URL } from "@/lib/assets";

const BLOCKS = [
  {
    icon: Moon,
    title: "The Serenity Hour",
    vibe: "Atmospheric · Gothic Rock · Post-Rock · Shoegaze",
    desc: "Cinematic, reverb-drenched and slow-burning. The comedown and the ascent.",
  },
  {
    icon: Flame,
    title: "Mid-Day Heavy",
    vibe: "Modern Rock · Nu-Metal · Metalcore · High-Energy",
    desc: "The driving core of the rotation. Grooves, breakdowns and hooks.",
  },
  {
    icon: Skull,
    title: "The Sorrow Block",
    vibe: "Deathcore · Technical Death Metal · Extreme · Industrial",
    desc: "The heaviest end of the spectrum. Uncompromising and abrasive.",
  },
];

export default function Radio() {
  return (
    <div data-testid="radio-page" className="pt-20">
      {/* HERO */}
      <section className="relative overflow-hidden py-20 md:py-28 vignette">
        <img src={TEXTURES.gothic} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-charcoal/70" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-4 md:px-12 lg:px-16 flex flex-col items-center text-center">
          <motion.img
            src={ASSETS.kssrLogo}
            alt="KSSR Radio"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="w-full max-w-md object-contain"
          />
          <Reveal delay={0.3}>
            <p className="mt-6 font-label uppercase tracking-[0.3em] text-slate text-sm">
              100% Unsigned Rock, Metal &amp; Alternative Broadcast
            </p>
          </Reveal>
        </div>
      </section>

      {/* PLAYER */}
      <section className="relative z-10 bg-charcoal pb-24">
        <div className="max-w-3xl mx-auto px-4 md:px-8 -mt-4">
          <Reveal>
            <RadioPlayer />
          </Reveal>
        </div>
      </section>

      {/* SCHEDULE */}
      <section className="relative z-10 bg-charcoal pb-24 md:pb-32">
        <div className="max-w-[1400px] mx-auto px-4 md:px-12 lg:px-16">
          <Reveal>
            <p className="font-label uppercase tracking-[0.35em] text-crimson-bright text-xs mb-4">Rotation Blocks</p>
            <h2 className="font-display text-3xl md:text-5xl text-softwhite">Station Programming</h2>
          </Reveal>

          <div className="mt-14 grid md:grid-cols-3 gap-px bg-slate/15 border border-slate/15">
            {BLOCKS.map((b, i) => {
              const Icon = b.icon;
              return (
                <Reveal key={b.title} delay={i * 0.12} className="bg-charcoal">
                  <div className="p-8 md:p-10 h-full group hover:bg-surface transition-colors duration-500">
                    <Icon size={30} className="text-crimson-bright" />
                    <h3 className="mt-6 font-display text-2xl text-softwhite">{b.title}</h3>
                    <p className="mt-3 font-label uppercase tracking-[0.12em] text-[11px] text-crimson-bright/80">
                      {b.vibe}
                    </p>
                    <p className="mt-4 text-slate leading-relaxed font-body">{b.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SUBMISSION CALLOUT */}
      <section className="relative z-10 overflow-hidden py-24">
        <img src={TEXTURES.crowd} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-charcoal/85" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-12 text-center">
          <Reveal>
            <h2 className="font-display text-4xl md:text-6xl text-softwhite leading-tight">
              ARE YOU AN <span className="text-crimson-bright">UNSIGNED</span> BAND?
            </h2>
            <p className="mt-6 max-w-2xl mx-auto text-slate text-lg font-body">
              Get your music in front of heavy music fans worldwide. No label fees, no PRO gatekeeping — just direct
              consent airplay.
            </p>
            <a
              href={GOOGLE_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="radio-submit-btn"
              className="mt-10 group inline-flex items-center gap-3 bg-crimson hover:bg-crimson-bright text-softwhite px-8 py-4 font-label uppercase tracking-[0.15em] text-sm transition-colors duration-300"
            >
              Submit Your Music to KSSR
              <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
